import { useTranslation } from "@privacy/translations";
import { DynamicGrid } from "../../dynamicGrid";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { InformationTypeIcon2 } from "../../shared/icons";
import { EmptyState } from "../../shared/EmptyState";
import {
  informationTypeByIdQueryOptions,
  informationTypeFormQueryOptions,
  informationTypesGridViewQueryOptions,
} from "../../api/informationTypes";
import { classificationTemplateQueryOptions } from "../../api/classificationTemplate";
import { PageTitle } from "../../shared/Page";
import { HasPermission } from "../../shared/permissions";
import { CreateInformationTypeDialog } from "./CreateInformationTypeDialog";
import { InformationTypeMainDrawer } from "./InformationTypeMainDrawer";
import { InformationTypeHistoryDrawer } from "./InformationTypeHistoryDrawer";
import { InformationTypeActionMenu } from "./InformationTypeDrawerHeader";
import { criticalityColumn } from "../../classification/criticalityColumn";
import { classificationScoresColumn } from "../../classification/classificationScoresColumn";
import { prefetch } from "../../api/prefetch";
import { getCurrentOrganisation, useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { prefetchUsersFromCurrentOrganisation, useGetUsersFromCurrentOrganisation } from "../../api/users";
import { useQuery } from "@tanstack/react-query";

const InformationTypesGridPage = () => {
  usePrivacyDocumentTitle("InformationType.Sidebar.InformationTypes");
  const { t } = useTranslation();
  const gridViewQuery = useQuery(informationTypesGridViewQueryOptions());
  const formQuery = useQuery(informationTypeFormQueryOptions());
  const usersQuery = useGetUsersFromCurrentOrganisation();
  const { navigate, paths } = usePrivacyNavigate();
  const { classificationEnabled } = useCurrentOrganisation();
  const templateQuery = useQuery({ ...classificationTemplateQueryOptions(), enabled: classificationEnabled });

  return (
    <DynamicGrid.Grid
      rowsData={gridViewQuery.data?.informationTypes}
      showArchivedFilter
      form={formQuery.data}
      getRowId={row => row.id}
      asyncQuestionCardData={{ usersQueryData: usersQuery.data }}
      getColumns={questionCardColumns => [
        ...(classificationEnabled
          ? [
              classificationScoresColumn(t, templateQuery.data?.template, row => row.classification?.scores ?? []),
              criticalityColumn(t, row => row.classification?.criticality),
            ]
          : []),
        ...questionCardColumns,
      ]}
      mainColumnProps={{
        headerName: t("InformationType.Grid.NameHeader"),
        renderLabel: row => row.name,
        renderIcon: ({ iconProps }) => <InformationTypeIcon2 {...iconProps} />,
      }}
      onRowSelect={id => navigate(paths.informationTypesInline(id))}
      Toolbar={InformationTypesGridToolbar}
      Footer={InformationTypesGridFooter}
      NoRowsOverlay={NoInformationTypesOverlay}
      texts={{ searchFieldPlaceholder: t("InformationType.Grid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <InformationTypeActionMenu
          informationTypeName={row.name}
          informationTypeId={row.id}
          archivedAt={row.archivedAt}
          onHover={() => prefetch(informationTypeByIdQueryOptions(row.id))}
          onDeleted={() => {
            if (selectedRowId === row.id) navigate(paths.informationTypesGrid());
          }}
        />
      )}
    />
  );
};

const InformationTypesGridToolbar = () => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  return (
    <DynamicGrid.Toolbar
      pageTitle={<PageTitle title={t("InformationType.Grid.Title")} subTitle={t("InformationType.Grid.SubTitle")} />}
      actionButtons={
        <>
          <DynamicGrid.ExportButton />

          <HasPermission permission="nova-forms-api:information-type:create">
            <DynamicGrid.CreateNewButton
              labelKey="InformationType.Create.Button"
              renderDialog={({ onClose }) => (
                <CreateInformationTypeDialog
                  onClose={onClose}
                  onCreated={informationType => navigate(paths.informationTypesInline(informationType.id))}
                />
              )}
            />
          </HasPermission>
        </>
      }
      filterButtons={
        <>
          <DynamicGrid.ColumnFilterButton />
          <DynamicGrid.FilterButton />
        </>
      }
    />
  );
};

const InformationTypesGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="InformationType.Grid.NumberOfRows" />;

const NoInformationTypesOverlay = () => <EmptyState labelKey="InformationType.NoRows" />;

export const informationTypesGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <InformationTypesGridPage />,
  rowDrawerRoutes: [
    { index: true, element: <InformationTypeMainDrawer /> },
    { path: "history", element: <InformationTypeHistoryDrawer /> },
  ],
  gridPrefetcher: () => void prefetchInformationTypesGridViewAndForm(),
  rowDrawerDefaultPrefetcher: ({ rowId }) => void prefetch(informationTypeByIdQueryOptions(rowId)),
});

export const prefetchInformationTypesGridViewAndForm = () => {
  prefetch(informationTypesGridViewQueryOptions());
  prefetch(informationTypeFormQueryOptions());
  prefetchUsersFromCurrentOrganisation();
  getCurrentOrganisation().then(currentOrganisation => {
    if (currentOrganisation?.classificationEnabled) prefetch(classificationTemplateQueryOptions());
  });
};
